// components/AnnouncementPopup.tsx
import React, { useState, useEffect } from 'react';
import { X, Info, AlertTriangle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

interface TranslationContent {
  title: string;
  message: string;
  button: string;
}

interface AnnouncementConfig {
  enabled: boolean;
  type: 'info' | 'warning' | 'alert';
  showOnce: boolean;
  dismissable: boolean;
  translations: {
    fi: TranslationContent;
    en: TranslationContent;
    sv: TranslationContent;
  };
}

const STORAGE_KEY = 'announcement_dismissed_hash';

// Simple hash function to detect message changes
const hashMessage = (translations: AnnouncementConfig['translations']): string => {
  const str = JSON.stringify(translations);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString();
};

const AnnouncementPopup: React.FC = () => {
  const { language } = useLanguage();
  const [config, setConfig] = useState<AnnouncementConfig | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [currentHash, setCurrentHash] = useState<string>('');

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch('/announcement-config.json');
        if (response.ok) {
          const data: AnnouncementConfig = await response.json();

          if (!data.enabled) return;

          // Generate hash of all translations
          const messageHash = hashMessage(data.translations);
          setCurrentHash(messageHash);

          // Check if this specific message was already dismissed
          if (data.showOnce) {
            const dismissedHash = localStorage.getItem(STORAGE_KEY);
            if (dismissedHash === messageHash) return;
          }

          setConfig(data);
          setTimeout(() => setIsVisible(true), 100);
        }
      } catch (error) {
        console.log('Announcement config not loaded');
      }
    };

    fetchConfig();
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      if (config?.showOnce && currentHash) {
        localStorage.setItem(STORAGE_KEY, currentHash);
      }
      setConfig(null);
    }, 300);
  };

  if (!config || !isVisible) return null;

  // Get translation for current language
  const content = config.translations[language] || config.translations.fi;

  const getTypeStyles = () => {
    switch (config.type) {
      case 'warning':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          icon: <AlertTriangle className="w-6 h-6 text-amber-600" />,
          titleColor: 'text-amber-800',
          textColor: 'text-amber-700'
        };
      case 'alert':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: <AlertCircle className="w-6 h-6 text-red-600" />,
          titleColor: 'text-red-800',
          textColor: 'text-red-700'
        };
      default:
        return {
          bg: 'bg-cyan-50',
          border: 'border-cyan-200',
          icon: <Info className="w-6 h-6 text-cyan-600" />,
          titleColor: 'text-cyan-800',
          textColor: 'text-cyan-700'
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={config.dismissable ? handleClose : undefined}
    >
      <div
        className={`${styles.bg} ${styles.border} border-2 rounded-2xl shadow-2xl max-w-lg w-full p-6 transform transition-all duration-300 ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-0.5">
            {styles.icon}
          </div>
          <div className="flex-1">
            <h3 className={`text-lg font-bold ${styles.titleColor} mb-2`}>
              {content.title}
            </h3>
            <p className={`${styles.textColor} whitespace-pre-line`}>
              {content.message}
            </p>
          </div>
          {config.dismissable && (
            <button
              onClick={handleClose}
              className="flex-shrink-0 p-1 hover:bg-gray-200 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          )}
        </div>

        {config.dismissable && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium"
            >
              {content.button}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementPopup;
